import { useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { Loader2, AlertTriangle } from "lucide-react";
import type { ChatMessage } from "@/lib/types";
import { useAuth } from "@/hooks/use-auth";
import { apiUrl } from "@/lib/config";

interface TranslatedMessageProps {
  message: ChatMessage;
  userLanguage: string;
  isOwn: boolean;
  showTranslation?: boolean;
  // Controlled by parent Eye button in message header
  showOriginal?: boolean;
  onToggleOriginal?: () => void;
}

const FAILURE_LABELS: Record<string, string> = {
  throttled: "Translation temporarily unavailable (rate limited)",
  unavailable: "Translation service is unavailable",
  overloaded: "Translation servers are busy — resend to try again",
  quota_exceeded: "Translation quota exceeded — try again later",
  error: "Translation failed",
};

export function TranslatedMessage({
  message,
  userLanguage,
  isOwn,
  showTranslation = true,
  showOriginal: controlledShowOriginal,
  onToggleOriginal,
}: TranslatedMessageProps) {
  const { user } = useAuth();
  const [internalShowOriginal, setInternalShowOriginal] = useState(false);
  const [clientTranslation, setClientTranslation] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [clientTranslationFailed, setClientTranslationFailed] = useState(false);
  const [clientFailureReason, setClientFailureReason] = useState<string | undefined>();
  // Tracks which messageId+language we've already attempted to translate — prevents infinite
  // re-fetch loops when the API returns the same text or throws an error.
  const fetchedForRef = useRef<string | null>(null);

  // Use controlled state when parent provides it (Eye button in header)
  const isControlled = controlledShowOriginal !== undefined;
  const showOriginal = isControlled ? controlledShowOriginal : internalShowOriginal;

  const handleToggle = () => {
    if (isControlled) {
      onToggleOriginal?.();
    } else {
      setInternalShowOriginal((prev) => !prev);
    }
  };

  // Prefer a cached translation in the user's *current* language. The
  // previous logic treated `message.isTranslated && originalContent` as
  // "already translated for this user" — but that only meant the server
  // translated it once at insert time, into whatever the user's language
  // was at THAT moment. After a language change, message.content holds a
  // stale-language translation, so we need to look up the new language in
  // the translations cache (or fetch it).
  const cachedTranslation = message.translations?.[userLanguage];
  const serverHasCurrentLang = !!cachedTranslation;
  const effectiveTranslation = cachedTranslation || clientTranslation;
  const hasTranslation = !!effectiveTranslation;

  // Combine server-side and client-side failure info
  const translationFailed = message.translationFailed || clientTranslationFailed;
  const failureReason = message.failureReason || clientFailureReason;

  // Client-side fallback / language-change refetch: request a translation
  // whenever we don't already have one for the *current* user language.
  // Own messages are translated too — when the user changes language
  // post-send, their own message should re-translate just like received
  // ones.
  useEffect(() => {
    const fetchKey = `${message.messageId}:${userLanguage}`;
    if (
      !showTranslation ||
      serverHasCurrentLang ||
      fetchedForRef.current === fetchKey ||
      !message.content?.trim()
    ) return;

    // Language changed — clear stale client translation before refetching.
    if (clientTranslation && fetchedForRef.current && !fetchedForRef.current.endsWith(`:${userLanguage}`)) {
      setClientTranslation(null);
      setClientTranslationFailed(false);
      setClientFailureReason(undefined);
    }

    // Mark as attempted immediately so re-renders caused by setIsTranslating
    // don't schedule a second fetch for the same message.
    fetchedForRef.current = fetchKey;

    const timer = setTimeout(async () => {
      setIsTranslating(true);
      try {
        // Send the original text when available so we don't double-translate
        // a stale-language version of the message back into another language.
        const sourceText = message.originalContent?.trim() || message.content;
        const res = await fetch(apiUrl("/api/translate"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: sourceText,
            targetLanguage: userLanguage,
            messageId: message.messageId,
            requesterId: user?.uid,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.translationFailed) {
            setClientTranslationFailed(true);
            setClientFailureReason(data.failureReason);
            Sentry.captureMessage("Translation failed (client fallback)", {
              level: "warning",
              extra: {
                messageId: message.messageId,
                targetLanguage: userLanguage,
                reason: data.failureReason,
              },
            });
          } else if (data.translatedText) {
            // Record the translation even when it matches message.content.
            // The server already translates at send time and stores the result
            // in `content`, so the refetch usually returns byte-identical text.
            // Skipping the state update in that case left `hasTranslation`
            // false, which took the no-translation render path and hid the
            // "Show original" toggle on exactly the messages that have an
            // original to show. No re-render loop: the fetchedForRef guard
            // above short-circuits the pass this setState triggers.
            setClientTranslation(data.translatedText);
          }
        }
      } catch {
        setClientTranslationFailed(true);
        setClientFailureReason('error');
      } finally {
        setIsTranslating(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  // isTranslating intentionally excluded: including it caused an infinite loop
  // because setIsTranslating(true/false) would re-trigger this effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.messageId, message.content, showTranslation, serverHasCurrentLang, clientTranslation, userLanguage]);

  // Translation failed indicator
  const failureBanner = translationFailed && !isOwn && (
    <div className={`text-xs mb-1 flex items-center space-x-1 ${isOwn ? "text-white/70" : "text-amber-500 dark:text-amber-400"}`}>
      <AlertTriangle className="w-3 h-3 shrink-0" />
      <span>{FAILURE_LABELS[failureReason || 'error'] || FAILURE_LABELS.error}</span>
    </div>
  );

  // Still translating or no translation available — show original
  if (!hasTranslation) {
    return (
      <div className="min-w-0">
        {isTranslating && !isOwn && (
          <div className={`text-xs mb-1 flex items-center space-x-1 ${isOwn ? "text-white/70" : "text-muted-foreground"}`}>
            <Loader2 className="w-3 h-3 animate-spin shrink-0" />
            <span>Translating…</span>
          </div>
        )}
        {failureBanner}
        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words [word-break:break-word] hyphens-auto">
          {message.content}
        </p>
      </div>
    );
  }

  // Only offer the toggle when there is a distinct original to reveal. A
  // message sent in the viewer's own language has no originalContent, so a
  // toggle there would swap the text for itself.
  const originalText = message.originalContent?.trim();
  const canShowOriginal = !!originalText && originalText !== effectiveTranslation;

  return (
    <div className="min-w-0">
      <p className="text-sm leading-relaxed whitespace-pre-wrap break-words [word-break:break-word] hyphens-auto">
        {showOriginal && canShowOriginal ? originalText : effectiveTranslation}
      </p>

      {canShowOriginal && (
        <span
          role="button"
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(e) => e.key === "Enter" && handleToggle()}
          className={`inline-block mt-1 text-[12px] leading-none cursor-pointer select-none opacity-50 hover:opacity-80 transition-opacity ${isOwn ? "text-white" : "text-muted-foreground"} ${
            showOriginal
              ? "text-[#E84B4B]"
              : "text-[var(--bubble-own-to)]"
          }`}
        >
          {showOriginal ? "Show Translated" : `Show original`}
        </span>
      )}
    </div>
  );
}
